import { Injectable, inject, signal } from '@angular/core';
import { AuthService } from '../../../core/auth/auth.service';
import { ChangeEvent, Followable, Sector } from '../domain/regs-models';
import { RegsApiService } from './regs-api.service';

export interface FollowPrefs {
  kinds: string[]; // 'rules' | 'quota' | 'headsup'
  push: boolean;
  popup: boolean;
  since: string;
}

export type FollowResult = 'followed' | 'followedNoPush' | 'unfollowed';

@Injectable({ providedIn: 'root' })
export class RegsFollowService {
  private api = inject(RegsApiService);
  private auth = inject(AuthService);

  private readonly followsMap = new Map<string, FollowPrefs>();
  private readonly seenIds = new Set<string>();
  private readonly seenText = new Set<string>();

  readonly recentChanges = signal<ChangeEvent[]>([]);
  readonly pendingPopups = signal<ChangeEvent[]>([]);
  readonly followedIds = signal<string[]>([]);
  readonly unreadCount = signal(0);
  readonly openRequest = signal<{ speciesId: string; sector: Sector } | null>(null);

  get ids(): string[] {
    return this.followedIds();
  }

  get recent(): ChangeEvent[] {
    return this.recentChanges();
  }

  get pending(): ChangeEvent[] {
    return this.pendingPopups();
  }

  constructor() {
    this.load();
  }

  private get storageKeyPrefix(): string {
    const uid = this.auth.getUserObjectId() || this.auth.getUserIdFromToken();
    return uid ? `keep_it.follows.${uid}` : 'keep_it.follows';
  }

  private get seenKey(): string {
    return `${this.storageKeyPrefix}.seen`;
  }

  private get seenTextKey(): string {
    return `${this.storageKeyPrefix}.seen_text`;
  }

  load(): void {
    try {
      const raw = localStorage.getItem(this.storageKeyPrefix);
      if (raw) {
        const parsed = JSON.parse(raw);
        for (const [k, v] of Object.entries(parsed)) {
          this.followsMap.set(k, v as FollowPrefs);
        }
      }

      const rawSeen = localStorage.getItem(this.seenKey);
      if (rawSeen) {
        const parsed = JSON.parse(rawSeen);
        if (Array.isArray(parsed)) {
          for (const id of parsed) this.seenIds.add(String(id));
        }
      }

      const rawSeenText = localStorage.getItem(this.seenTextKey);
      if (rawSeenText) {
        const parsed = JSON.parse(rawSeenText);
        if (Array.isArray(parsed)) {
          for (const txt of parsed) this.seenText.add(String(txt));
        }
      }
    } catch (e) {
      console.warn('RegsFollowService: error loading from localStorage', e);
    }

    this.followedIds.set(Array.from(this.followsMap.keys()).sort());
    this.check();
  }

  private save(): void {
    try {
      const obj: Record<string, FollowPrefs> = {};
      for (const [k, v] of this.followsMap.entries()) {
        obj[k] = v;
      }
      localStorage.setItem(this.storageKeyPrefix, JSON.stringify(obj));
      localStorage.setItem(this.seenKey, JSON.stringify(Array.from(this.seenIds)));
      localStorage.setItem(this.seenTextKey, JSON.stringify(Array.from(this.seenText)));
    } catch (e) {
      console.warn('RegsFollowService: error saving to localStorage', e);
    }
    this.followedIds.set(Array.from(this.followsMap.keys()).sort());
  }

  isFollowing(speciesId: string, s: Sector): boolean {
    return this.followsMap.has(Followable.idFor(speciesId, s));
  }

  prefsFor(speciesId: string, s: Sector): FollowPrefs | undefined {
    return this.followsMap.get(Followable.idFor(speciesId, s));
  }

  async follow(speciesId: string, sector: Sector, prefs: FollowPrefs): Promise<FollowResult> {
    const id = Followable.idFor(speciesId, sector);
    this.followsMap.set(id, prefs);
    this.save();
    this.check();
    return 'followed';
  }

  async unfollow(speciesId: string, sector: Sector): Promise<void> {
    const id = Followable.idFor(speciesId, sector);
    this.followsMap.delete(id);
    this.save();
    this.check();
  }

  private fingerprint(e: ChangeEvent): string {
    return [e.follow, e.kind, e.title, e.text].join('|');
  }

  isNew(e: ChangeEvent): boolean {
    return !this.seenIds.has(e.id) && !this.seenText.has(this.fingerprint(e));
  }

  markAllSeen(): void {
    for (const e of this.recentChanges()) {
      this.seenIds.add(e.id);
      this.seenText.add(this.fingerprint(e));
    }
    this.save();
    this.pendingPopups.set([]);
    this.unreadCount.set(0);
  }

  dismissAll(): void {
    for (const e of this.pendingPopups()) {
      this.seenIds.add(e.id);
      this.seenText.add(this.fingerprint(e));
    }
    this.save();
    this.pendingPopups.set([]);
    this.unreadCount.set(0);
  }

  requestOpen(speciesId: string, sector: Sector): void {
    this.openRequest.set({ speciesId, sector });
  }

  clearOpenRequest(): void {
    this.openRequest.set(null);
  }

  async check(): Promise<void> {
    const ids = Array.from(this.followsMap.keys());
    if (ids.length === 0) {
      this.recentChanges.set([]);
      this.pendingPopups.set([]);
      this.unreadCount.set(0);
      return;
    }

    try {
      const events = await this.api.changes(ids);
      this.recentChanges.set(events);

      const pending = events.filter((e) => {
        const prefs = this.followsMap.get(e.follow);
        if (!prefs || !prefs.popup) return false;
        return this.isNew(e);
      });

      this.pendingPopups.set(pending);
      this.unreadCount.set(pending.length);
    } catch (e) {
      console.warn('RegsFollowService: error checking changes', e);
    }
  }
}
